import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, ChevronRight,
  Loader2, AlertTriangle, Check, Copy, Archive, FileText, X
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { ukDate } from "../lib/format";
import { templateErrorMessage, templateConflict } from "../lib/jobTemplates";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { TemplateLinesEditor, computeTemplatePhase } from "../components/TemplateLines";
import { gbp } from "../lib/format";
import { newId } from "../lib/estimate";
import {
  Card, Field, Input, Textarea, PrimaryButton, QuietButton, GhostButton,
  StickyHeader
} from "../components/UI";

/* Pure helper — strips runtime/UI bits and normalises sequence + sub-phase strings.
 * LT-21b — lines keep only the library reference + default qty (+ display cache);
 * a line targeting a sub-phase that no longer exists falls back to the phase root. */
function serialiseForSave(t) {
  return {
    name: t.name,
    description: t.description || "",
    job_type: t.job_type || "",
    phases: (t.phases || []).map((p, i) => {
      const subs = (p.sub_phases || []).filter((s) => s && s.trim().length > 0);
      return {
        phase_name: p.phase_name,
        sequence: i + 1,
        sub_phases: subs,
        lines: (p.lines || []).map((l) => ({
          library_item_id: l.library_item_id,
          qty: Number(l.qty) || 1,
          sub_phase: l.sub_phase && subs.includes(l.sub_phase) ? l.sub_phase : null,
          description: l.description || null,
          unit: l.unit || null,
          sku: l.sku || null,
        })),
      };
    }),
  };
}

/* ===================== save badge ===================== */
function SaveBadge({ status, lastSaved }) {
  let label, bg, fg, dot, Icon;
  if (status === "saving") {
    label = "Saving…"; bg = "#FEF3E2"; fg = "#B45309"; dot = "#D97706"; Icon = Loader2;
  } else if (status === "error") {
    label = "Save failed"; bg = "#F4E6E4"; fg = "#9B3A37"; dot = "#B91C1C"; Icon = AlertTriangle;
  } else if (status === "saved") {
    label = lastSaved ? `Saved at ${lastSaved}` : "All changes saved";
    bg = "#E8EFE9"; fg = "#2F4F3E"; dot = "#3F6E55"; Icon = Check;
  } else {
    label = "Idle"; bg = "#F0EEE6"; fg = "#6B6B6B"; dot = "#A0A0A0"; Icon = null;
  }
  return (
    <span
      data-testid="template-save-status"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
      style={{ background: bg, color: fg, fontSize: 11, fontWeight: 500, border: `1px solid ${dot}22` }}
    >
      {Icon ? <Icon size={11} className={status === "saving" ? "animate-spin" : ""} />
        : <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
      {label}
    </span>
  );
}

/* ===================== sub-phase row ===================== */
function PhaseCard({ phase, index, totalPhases, onChange, onRemove, onMoveUp, onMoveDown, masterPhaseNames }) {
  const [showMasterPicker, setShowMasterPicker] = useState(false);
  const totals = computeTemplatePhase(phase);

  const addSubPhase = (name = "") => {
    onChange({ ...phase, sub_phases: [...(phase.sub_phases || []), name] });
  };
  // master phases not yet used in this template phase
  const availableMaster = (masterPhaseNames || []).filter(
    (n) => !(phase.sub_phases || []).map((s) => s.toLowerCase()).includes(n.toLowerCase())
  );

  return (
    <Card className="overflow-visible">
      <div className="flex items-start gap-3 px-4 py-3 border-b border-line bg-surface-2/50">
        <span className="font-mono tnum text-ink-3 mt-1.5 flex-shrink-0" style={{ fontSize: 11.5 }}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={phase.phase_name}
            spellCheck="true"
            placeholder="Phase name"
            onChange={(e) => onChange({ ...phase, phase_name: e.target.value })}
            data-testid={`phase-name-${index}`}
            className="w-full bg-transparent border-0 outline-none rounded px-2 py-1 focus:bg-bg focus:ring-1 focus:ring-accent/40 font-display text-ink"
            style={{ fontSize: 16, fontWeight: 600 }}
          />
          <p className="text-ink-3 ml-2 mt-0.5" style={{ fontSize: 11.5 }}>
            {(phase.lines || []).length} default line{(phase.lines || []).length === 1 ? "" : "s"}
            {" · "}{(phase.sub_phases || []).length} sub-phase{(phase.sub_phases || []).length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 mt-1">
          {totals.line_count > 0 && (
            <span className="font-mono tnum text-ink-3" style={{ fontSize: 11.5 }} data-testid={`phase-${index}-header-total`}>
              {totals.line_count} item{totals.line_count === 1 ? "" : "s"}
              <span className="text-ink ml-2" style={{ fontWeight: 600 }}>{gbp(totals.phase_total)}</span>
            </span>
          )}
          <div className="flex items-center gap-1">
          <button type="button" onClick={onMoveUp} disabled={index === 0}
            className="p-1.5 rounded-md hover:bg-surface text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move phase up">
            <ChevronUp size={13} />
          </button>
          <button type="button" onClick={onMoveDown} disabled={index === totalPhases - 1}
            className="p-1.5 rounded-md hover:bg-surface text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move phase down">
            <ChevronDown size={13} />
          </button>
          <button type="button" onClick={onRemove}
            data-testid={`phase-remove-${index}`}
            className="p-1.5 rounded-md hover:bg-surface text-ink-3 hover:text-note-red"
            title="Remove phase">
            <Trash2 size={13} />
          </button>
          </div>
        </div>
      </div>

      {/* Default library lines lead — laid out as the estimate they become
          (sub-phases render as header rows inside the table). */}
      <TemplateLinesEditor phase={phase} phaseIndex={index} onChange={onChange} />

      {/* Sub-phase controls — structural, so one discreet row. */}
      <div className="px-4 py-2.5 border-t border-line-soft" data-testid={`phase-${index}-sub-phases`}>
          <div className="flex items-center gap-3">
            <span className="col-head text-ink-3" style={{ fontSize: 9.5 }}>
              Sub-phases · {(phase.sub_phases || []).length}
            </span>
            <button
              type="button"
              onClick={() => addSubPhase("")}
              data-testid={`phase-${index}-add-sub`}
              className="inline-flex items-center gap-1 text-ink-3 hover:text-accent"
              style={{ fontSize: 11 }}
            >
              <Plus size={11} /> Add sub-phase
            </button>
            {availableMaster.length > 0 && (
              <button
                type="button"
                onClick={() => setShowMasterPicker((s) => !s)}
                data-testid={`phase-${index}-pick-master`}
                className="inline-flex items-center gap-1 text-ink-3 hover:text-accent"
                style={{ fontSize: 11 }}
              >
                <Plus size={11} /> Pick from master list
              </button>
            )}
            {(phase.sub_phases || []).length === 0 && (
              <span className="text-ink-4" style={{ fontSize: 11 }}>· optional — lines can sit at the phase root</span>
            )}
          </div>

          {showMasterPicker && availableMaster.length > 0 && (
            <div
              data-testid={`phase-${index}-master-picker`}
              className="mt-2 p-2 rounded-md border border-line bg-bg"
            >
              <div className="flex flex-wrap gap-1.5">
                {availableMaster.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { addSubPhase(n); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-line bg-surface hover:bg-accent-soft hover:text-accent hover:border-accent text-ink-2 transition-colors"
                    style={{ fontSize: 11.5 }}
                  >
                    <Plus size={10} /> {n}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex justify-end">
                <button type="button" onClick={() => setShowMasterPicker(false)}
                  className="text-ink-4 hover:text-ink-2" style={{ fontSize: 11 }}>
                  Done
                </button>
              </div>
            </div>
          )}

      </div>
    </Card>
  );
}

/* ===================== main editor ===================== */
export default function TemplateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [masterPhases, setMasterPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [lastSaved, setLastSaved] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showAddPhase, setShowAddPhase] = useState(false);
  const { activeTypes, labelFor } = useJobTypeLibrary();

  const lastSavedJsonRef = useRef("");
  const saveTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, mpRes] = await Promise.all([
        api.get(`/templates/${id}`),
        api.get("/settings/master-phases"),
      ]);
      const t = tplRes.data;
      // Normalise phases (older payloads may have only phase_name + sequence; pre-LT-21b have no lines)
      t.phases = (t.phases || []).map((p, i) => ({
        phase_name: p.phase_name || "",
        sequence: p.sequence ?? i + 1,
        sub_phases: Array.isArray(p.sub_phases) ? p.sub_phases : [],
        // `_key` is a transient client id for drag-and-drop / React keys.
        lines: Array.isArray(p.lines) ? p.lines.map((l) => ({ ...l, _key: l._key || newId() })) : [],
      }));
      setTemplate(t);
      lastSavedJsonRef.current = JSON.stringify(serialiseForSave(t));
      setMasterPhases((mpRes.data || []).filter((m) => !m.is_archived));
    } catch (e) {
      toast.error("Failed to load template");
      navigate("/templates");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const triggerSave = useCallback(async (next) => {
    if (!next || !next.id) return;
    const payload = serialiseForSave(next);
    const json = JSON.stringify(payload);
    if (json === lastSavedJsonRef.current) return;
    if (!payload.name || !payload.name.trim()) {
      setSaveStatus("error");
      toast.error("Template name is required");
      return;
    }
    setSaveStatus("saving");
    const patch = (replace) => api.patch(`/templates/${next.id}${replace ? "?replace=true" : ""}`, payload);
    try {
      let res;
      try {
        res = await patch(false);
      } catch (e) {
        // LT-21b — one live template per job type. Replace explicitly or revert.
        const c = templateConflict(e);
        if (!c) throw e;
        const ok = window.confirm(
          `"${c.name}" is already the template for ${labelFor(c.job_type)}. ` +
          `Replace it with "${payload.name}"? The old template is archived, not deleted.`
        );
        if (!ok) {
          const prev = lastSavedJsonRef.current ? JSON.parse(lastSavedJsonRef.current) : {};
          setTemplate((cur) => (cur ? { ...cur, job_type: prev.job_type || "" } : cur));
          setSaveStatus("saved");
          return;
        }
        res = await patch(true);
        toast.success(`Replaced "${c.name}" as the ${labelFor(c.job_type)} template — the old one is archived`);
      }
      lastSavedJsonRef.current = json;
      const now = new Date();
      setLastSaved(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
      setSaveStatus("saved");
      // Reflect server-side normalisation back into state without disturbing
      // focus: updated_at, plus the priced `_item` for any line whose live
      // item the server has now looked up (new picks arrive provisional).
      const priced = {};
      for (const p of res.data.phases || []) for (const tl of p.lines || []) if (tl._item) priced[tl.library_item_id] = tl._item;
      setTemplate((cur) => cur ? {
        ...cur,
        updated_at: res.data.updated_at,
        phases: (cur.phases || []).map((p) => ({
          ...p,
          lines: (p.lines || []).map((tl) => (priced[tl.library_item_id] ? { ...tl, _item: priced[tl.library_item_id] } : tl)),
        })),
      } : cur);
    } catch (e) {
      setSaveStatus("error");
      toast.error(templateErrorMessage(e, "Save failed"));
    }
  }, [labelFor]);

  // Debounced autosave
  useEffect(() => {
    if (!template) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => triggerSave(template), 800);
    return () => saveTimerRef.current && clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // ---- mutations ----
  const setField = (patch) => setTemplate((t) => ({ ...t, ...patch }));
  const setPhase = (index, next) => {
    setTemplate((t) => ({
      ...t,
      phases: t.phases.map((p, i) => (i === index ? next : p)),
    }));
  };
  const removePhase = (index) => {
    if (!window.confirm("Remove this phase from the template? Its sub-phases and default lines will be lost.")) return;
    setTemplate((t) => ({ ...t, phases: t.phases.filter((_, i) => i !== index) }));
  };
  const movePhase = (index, delta) => {
    setTemplate((t) => {
      const next = [...t.phases];
      const j = index + delta;
      if (j < 0 || j >= next.length) return t;
      [next[index], next[j]] = [next[j], next[index]];
      return { ...t, phases: next };
    });
  };
  const addPhase = (name) => {
    const v = (name || "").trim();
    if (!v) return;
    if ((template.phases || []).some((p) => p.phase_name.toLowerCase() === v.toLowerCase())) {
      toast.error("That phase is already in the template");
      return;
    }
    setTemplate((t) => ({
      ...t,
      phases: [...t.phases, { phase_name: v, sequence: t.phases.length + 1, sub_phases: [], lines: [] }],
    }));
    setShowAddPhase(false);
  };

  const onDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await api.post(`/templates/${template.id}/duplicate`);
      toast.success(`Duplicated as "${res.data.name}"`);
      navigate(`/templates/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    } finally {
      setDuplicating(false);
    }
  };

  const onArchive = async () => {
    if (!window.confirm(`Archive template "${template.name}"? It will be hidden from the list.`)) return;
    setArchiving(true);
    try {
      await api.delete(`/templates/${template.id}`);
      toast.success("Template archived");
      navigate("/templates");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
      setArchiving(false);
    }
  };

  // ---- render ----
  if (loading || !template) {
    return (
      <div className="app-container py-12 flex items-center justify-center">
        <Loader2 className="animate-spin text-ink-3" size={20} />
      </div>
    );
  }

  const usedPhaseNames = new Set((template.phases || []).map((p) => p.phase_name.toLowerCase()));
  const availableMasterPhases = masterPhases.filter((m) => !usedPhaseNames.has(m.name.toLowerCase()));
  const masterPhaseNames = masterPhases.map((m) => m.name);
  const totalLines = (template.phases || []).reduce((s, p) => s + (p.lines || []).length, 0);
  const jobTypeIsActive = !template.job_type || activeTypes.some((jt) => jt.key === template.job_type);

  return (
    <div className="app-container py-6" data-testid="template-edit-page">
      <StickyHeader
        breadcrumb={
          <>
            <button
              type="button"
              onClick={() => navigate("/templates")}
              data-testid="back-to-templates"
              className="inline-flex items-center gap-1 text-ink-3 hover:text-ink"
              style={{ fontSize: 12 }}
            >
              <ArrowLeft size={12} /> Job Templates
            </button>
            <ChevronRight size={11} />
          </>
        }
        title={template.name || "Untitled template"}
        status={<SaveBadge status={saveStatus} lastSaved={lastSaved} />}
        actions={
          <>
            <QuietButton icon={duplicating ? Loader2 : Copy} onClick={onDuplicate} disabled={duplicating} data-testid="duplicate-template">
              Duplicate
            </QuietButton>
            <QuietButton icon={archiving ? Loader2 : Archive} onClick={onArchive} disabled={archiving} data-testid="archive-template">
              Archive
            </QuietButton>
          </>
        }
      />

      {/* Title block */}
      <div className="mt-7 mb-6">
        <h1 className="font-display text-ink" style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.01em" }}>
          {template.name || "Untitled template"}
        </h1>
        <p className="text-ink-3 mt-1" style={{ fontSize: 13 }}>
          The phases, sub-phases and default library lines a tender starts with. Lines are priced from the live library the moment a tender is created.
          {template.updated_at && (
            <span className="ml-2 text-ink-4">· last updated {ukDate(template.updated_at)}</span>
          )}
        </p>
        <p className="mt-2" style={{ fontSize: 12 }} data-testid="template-default-status">
          {template.job_type ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line px-2.5 py-1 text-ink-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#3F6E55" }} />
              Default template for <strong>{labelFor(template.job_type)}</strong>
              {!jobTypeIsActive && <span className="text-ink-4">(job type archived — never auto-applied)</span>}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line-soft px-2.5 py-1 text-ink-3">
              Not tied to a job type — apply it manually from a tender's estimate
            </span>
          )}
        </p>
      </div>

      {/* Details card */}
      <Card className="mb-6">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Template details
          </h2>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Name" required>
            <Input
              value={template.name}
              onChange={(v) => setField({ name: v })}
              placeholder="e.g. Single Storey Extension"
              data-testid="template-name-input"
            />
          </Field>
          <Field label="Job type" hint="One live template per job type. New tenders of this type start from it automatically.">
            <select
              value={template.job_type || ""}
              onChange={(e) => setField({ job_type: e.target.value })}
              data-testid="template-job-type-select"
              className="w-full h-9 px-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            >
              <option value="">— No job type —</option>
              {activeTypes.map((jt) => (
                <option key={jt.key} value={jt.key}>{jt.label}</option>
              ))}
              {!jobTypeIsActive && (
                <option value={template.job_type}>{labelFor(template.job_type)} (archived)</option>
              )}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Description">
              <Textarea
                rows={2}
                value={template.description || ""}
                onChange={(v) => setField({ description: v })}
              />
            </Field>
          </div>
        </div>
      </Card>

      {/* Phases */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
          Phases, sub-phases & default lines
        </h2>
        <span className="text-ink-3" style={{ fontSize: 12 }} data-testid="template-structure-summary">
          {(template.phases || []).length} phase{(template.phases || []).length === 1 ? "" : "s"} ·
          {" "}
          {(template.phases || []).reduce((s, p) => s + (p.sub_phases || []).length, 0)} sub-phases ·
          {" "}
          {totalLines} default line{totalLines === 1 ? "" : "s"}
        </span>
      </div>

      <div className="space-y-3">
        {(template.phases || []).map((p, i) => (
          <PhaseCard
            key={i}
            index={i}
            phase={p}
            totalPhases={template.phases.length}
            masterPhaseNames={masterPhaseNames}
            onChange={(next) => setPhase(i, next)}
            onRemove={() => removePhase(i)}
            onMoveUp={() => movePhase(i, -1)}
            onMoveDown={() => movePhase(i, 1)}
          />
        ))}

        {(template.phases || []).length === 0 && !showAddPhase && (
          <Card>
            <div className="flex flex-col items-center text-center py-10 px-6">
              <div className="w-11 h-11 rounded-full bg-surface-2 flex items-center justify-center text-ink-3 mb-3">
                <FileText size={18} />
              </div>
              <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
                No phases yet
              </h3>
              <p className="text-ink-3 mt-1" style={{ fontSize: 12.5, maxWidth: 360 }}>
                Add the phases this template should pre-load. You can add sub-phases under each one.
              </p>
              <div className="mt-4">
                <PrimaryButton icon={Plus} onClick={() => setShowAddPhase(true)} data-testid="add-first-phase">
                  Add first phase
                </PrimaryButton>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Add phase footer — also the only add path when the template is empty */}
      {((template.phases || []).length > 0 || showAddPhase) && (
        <div className="mt-4">
          {showAddPhase ? (
            <Card className="overflow-visible">
              <div className="p-4">
                <p className="col-head text-ink-3 mb-2.5" style={{ fontSize: 9.5 }}>
                  Add a phase
                </p>
                {availableMasterPhases.length > 0 && (
                  <>
                    <p className="text-ink-3 mb-2" style={{ fontSize: 11.5 }}>
                      Pick from master list
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {availableMasterPhases.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => addPhase(m.name)}
                          data-testid={`add-phase-${m.name}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-line bg-surface hover:bg-accent-soft hover:text-accent hover:border-accent text-ink-2 transition-colors"
                          style={{ fontSize: 11.5 }}
                        >
                          <Plus size={11} /> {m.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <CustomPhaseInput onAdd={addPhase} onCancel={() => setShowAddPhase(false)} />
              </div>
            </Card>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddPhase(true)}
              data-testid="add-phase-button"
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-dashed border-line bg-surface text-ink-3 hover:text-accent hover:border-accent hover:bg-accent-soft transition-colors"
              style={{ fontSize: 12.5, fontWeight: 500 }}
            >
              <Plus size={13} /> Add phase
            </button>
          )}
        </div>
      )}

      <div className="h-16" />
    </div>
  );
}

/* ===================== custom phase input ===================== */
function CustomPhaseInput({ onAdd, onCancel }) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const submit = (e) => {
    e?.preventDefault();
    if (name.trim()) onAdd(name.trim());
    setName("");
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 pt-1 border-t border-line-soft">
      <p className="text-ink-3" style={{ fontSize: 11.5 }}>Or custom name</p>
      <input
        ref={inputRef}
        type="text"
        value={name}
        spellCheck="true"
        onChange={(e) => setName(e.target.value)}
        placeholder="Type a custom phase name and press Enter"
        data-testid="custom-phase-input"
        className="flex-1 h-8 px-2.5 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        style={{ fontSize: 12.5 }}
      />
      <PrimaryButton type="submit" icon={Plus} disabled={!name.trim()} data-testid="custom-phase-add">
        Add
      </PrimaryButton>
      <GhostButton onClick={onCancel}>Cancel</GhostButton>
    </form>
  );
}
